/* eslint-disable react/no-array-index-key */
import React from 'react';
import { get } from 'lodash';
import { object, oneOf, bool } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment-timezone';
import {
  getBookingDates,
  txIsEnquired,
  txIsEnquiryExpired,
  hasLodgingForTransaction,
  getPartySizeGuestCount,
} from '../../util/transaction';
import AddressLinkMaybe from './AddressLinkMaybe';
import css from './BookingDetails.module.css';
import { propTypes } from '../../util/types';
import SafeHtml from '../SafeHtml/SafeHtml';
import PackageItinerary from '../PackageItinerary/PackageItinerary';
import PricingDetails from './PricingDetails';
import { GLP_SELECTION } from '../../util/guestLiabilityProtection';

const BookingDetails = ({
  transaction,
  stateData,
  currentUser,
  isInquiry,
  transactionRole,
  glpSelection,
  isGuestLiabilityProtectionIncluded,
}) => {
  let bookingGuestSize = get(
    transaction,
    'attributes.protectedData.packageLineItem.guestSize',
    null
  );

  let bookingActivity = get(transaction, 'attributes.protectedData.packageLineItem.activity', null);

  // eslint-disable-next-line prefer-const
  let { bookingStart, bookingEnd, lodgingStartDate: startLodgingDate } = getBookingDates(
    transaction
  );

  const hasLodging = hasLodgingForTransaction(transaction);

  const currentOrExpiredInquiryState = txIsEnquired(transaction) || txIsEnquiryExpired(transaction);
  if (currentOrExpiredInquiryState) {
    bookingGuestSize = get(
      transaction,
      'attributes.protectedData.selectedPackage.packageParty',
      null
    );

    bookingActivity = get(
      transaction,
      'attributes.protectedData.selectedPackage.packageActivity',
      null
    );

    const inquiryStartDate = get(transaction, 'attributes.protectedData.startDate', null);
    const inquiryEndDate = get(transaction, 'attributes.protectedData.endDate', null);
    bookingStart = inquiryStartDate ? moment(inquiryStartDate) : null;
    bookingEnd = inquiryEndDate ? moment(inquiryEndDate) : null;
  }

  if (!bookingGuestSize) {
    // Special offers have no package line item, use the party size instead
    bookingGuestSize = getPartySizeGuestCount(transaction);
  }

  if (!bookingActivity) {
    bookingActivity = get(
      transaction,
      'attributes.protectedData.selectedPackage.packageActivity',
      null
    );
  }

  const provider = get(transaction, 'provider', null);
  const userIsProvider = provider?.id?.uuid === currentUser?.id?.uuid;

  const location = get(transaction, 'listing.attributes.publicData.location', null);
  const geolocation = get(transaction, 'listing.attributes.geolocation', null);

  const propertyAddress = location && geolocation && (
    <AddressLinkMaybe
      rootClassName={css.address}
      location={location}
      geolocation={geolocation}
      showAddress
    />
  );

  const arrivalInstructions = get(
    transaction,
    'listing.attributes.publicData.arrivalInstructions.instructions',
    null
  );

  const listingOnXWaypoints = get(transaction, 'listing.attributes.publicData.waypoints.onX', []);
  const listingHuntWiseWaypoints = get(
    transaction,
    'listing.attributes.publicData.waypoints.huntWise',
    []
  );

  const showPropertyAddress = stateData.showPropertyAddress && propertyAddress;
  const showArrivalInstructions = stateData.showArrivalInstructions && arrivalInstructions;
  const showListingOnXWaypoints =
    stateData.showListingWaypoints && listingOnXWaypoints && listingOnXWaypoints.length > 0;
  const showListingHuntWiseWaypoints =
    stateData.showListingWaypoints &&
    listingHuntWiseWaypoints &&
    listingHuntWiseWaypoints.length > 0;

  const showPropertyDetails =
    showPropertyAddress ||
    showArrivalInstructions ||
    showListingOnXWaypoints ||
    showListingHuntWiseWaypoints;

  const { showBookingDetails } = stateData;

  return (
    <div className={css.root}>
      <div className={css.bookingDates}>
        <PackageItinerary
          activity={bookingActivity}
          listing={transaction.listing}
          startDate={bookingStart.format('YYYY-MM-DD')}
          endDate={bookingEnd.format('YYYY-MM-DD')}
          hasLodging={hasLodging}
          bookingGuestSize={bookingGuestSize}
          isProvider={userIsProvider}
          startLodgingDate={startLodgingDate ? moment(startLodgingDate).format('YYYY-MM-DD') : null}
        />
      </div>

      {showBookingDetails && !isInquiry && (
        <>
          <div>
            <PricingDetails
              transaction={transaction}
              userRole={transactionRole}
              glpSelection={glpSelection}
              isGuestLiabilityProtectionIncluded={isGuestLiabilityProtectionIncluded}
            />
          </div>
        </>
      )}

      {showPropertyDetails && (
        <div className={css.header}>
          <div className={css.divider} />
        </div>
      )}

      {showPropertyDetails && (
        <div className={css.header}>
          <FormattedMessage id="BookingDetails.propertyDetails" />
        </div>
      )}

      {showPropertyAddress && (
        <div className={css.addressWrapper}>
          <div className={css.title}>
            <FormattedMessage id="BookingDetails.propertyAddress" />
          </div>
          <div>{propertyAddress}</div>
        </div>
      )}

      {showArrivalInstructions && (
        <div>
          <div className={css.title}>
            <FormattedMessage id="BookingDetails.arrivalInstructions" />
          </div>

          <SafeHtml className={css.description} html={arrivalInstructions} />
        </div>
      )}

      {(showPropertyAddress || showArrivalInstructions) &&
        (showListingOnXWaypoints || showListingHuntWiseWaypoints) && (
          <div className={css.divider} />
        )}

      {showListingOnXWaypoints && (
        <>
          <div className={css.title}>
            <FormattedMessage id="BookingDetails.onXWaypoints" />
          </div>

          <div className={css.waypoints}>
            {listingOnXWaypoints.map((waypoint, index) => (
              <a
                key={`waypoint-${index}`}
                className={css.waypointLink}
                href={waypoint.url}
                target="_blank"
                rel="noreferrer"
              >
                {waypoint.title}
              </a>
            ))}
          </div>
        </>
      )}

      {showListingHuntWiseWaypoints && (
        <>
          <div className={css.title}>
            <FormattedMessage id="BookingDetails.huntWiseWaypoints" />
          </div>

          <div className={css.waypoints}>
            {listingHuntWiseWaypoints.map((waypoint, index) => (
              <a
                key={`waypoint-${index}`}
                className={css.waypointLink}
                href={waypoint.url}
                target="_blank"
                rel="noreferrer"
              >
                {waypoint.title}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

BookingDetails.defaultProps = {
  glpSelection: undefined,
  isGuestLiabilityProtectionIncluded: false,
};

BookingDetails.propTypes = {
  transaction: propTypes.transaction.isRequired,
  glpSelection: oneOf([GLP_SELECTION.YES, GLP_SELECTION.NO, GLP_SELECTION.LT_PLUS]),
  isGuestLiabilityProtectionIncluded: bool,
  stateData: object.isRequired,
};

export default BookingDetails;
